import React, { useLayoutEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Collapse,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from "@material-ui/core";
import KeyboardArrowDownIcon from "@material-ui/icons/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@material-ui/icons/KeyboardArrowRight";
import KeyboardArrowUpIcon from "@material-ui/icons/KeyboardArrowUp";
import CustomAcordion from "../../layout/CustomAcordion";
import HaklaiResults2990Panel from "./form2990/HaklaiResults2990Panel";
import HaklaiNoTaxOutcome from "../HaklaiNoTaxOutcome";
import { COMPACT_CURRENCY_LABELS, HAKLAI_PTOR_TYPES } from "../calc/shared/haklaiConstants";
import { isHaklaiAnalysisFullyExempt } from "../domain/haklaiAnalysisOutcome";
import {
  HAKLAI_ANALYSIS_RESULTS_LABELS,
  HAKLAI_ANALYSIS_RESULTS_MESSAGES
} from "./haklaiAnalysisResultsTexts";
import {
  buildHaklaiPayableTaxDisplayByPropertyKey,
  formatHaklaiWholeMoney
} from "../calc/shared/haklaiMoneyHelpers";

/**
 * Owns the Haklai analysis result screen.
 *
 * Responsibilities:
 * - renders calculation summaries returned from analysis
 * - exposes the generated 2990 form panel
 * - keeps result formatting separate from the editable calculator sections
 */

const PROPERTY_TYPE_LABELS = {
  residential: HAKLAI_ANALYSIS_RESULTS_LABELS.residential,
  commercial: HAKLAI_ANALYSIS_RESULTS_LABELS.commercial,
  commercialBg: HAKLAI_ANALYSIS_RESULTS_LABELS.commercial,
  landAndShared: HAKLAI_ANALYSIS_RESULTS_LABELS.landAndShared,
  farmRemainder: HAKLAI_ANALYSIS_RESULTS_LABELS.farmRemainder
};

const PURCHASE_DEDUCTION_TYPES = new Set(["purchase", "inheritance", "gift"]);

const columns = [
  { key: "propertyType", label: HAKLAI_ANALYSIS_RESULTS_LABELS.propertyType, align: "right", frame: true },
  { key: "saleValue", label: HAKLAI_ANALYSIS_RESULTS_LABELS.saleValue, frame: true },
  { key: "nominalAcquisitionPrice", label: HAKLAI_ANALYSIS_RESULTS_LABELS.nominalAcquisitionPrice, pair: "start" },
  { key: "indexedAcquisitionPrice", label: HAKLAI_ANALYSIS_RESULTS_LABELS.indexedAcquisitionPrice, pair: "end" },
  { key: "nominalExpenses", label: HAKLAI_ANALYSIS_RESULTS_LABELS.nominalExpenses, pair: "start" },
  { key: "indexedExpenses", label: HAKLAI_ANALYSIS_RESULTS_LABELS.indexedExpenses, pair: "end" },
  { key: "depreciation", label: HAKLAI_ANALYSIS_RESULTS_LABELS.depreciation, frame: true },
  { key: "totalShevach", label: HAKLAI_ANALYSIS_RESULTS_LABELS.propertyShevach, isTotal: true, frame: true },
  { key: "capitalGainsTax", label: HAKLAI_ANALYSIS_RESULTS_LABELS.partTax, isTotal: true, frame: true }
];

function getResultsColumnClassName(column, { includeWidth = false } = {}) {
  return [
    includeWidth
      ? (column.key === "propertyType"
        ? "haklai-results-property-col"
        : `haklai-results-amount-col${column.key === "nominalExpenses" ? " haklai-results-expense-col" : ""}`)
      : "",
    column.pair ? "haklai-results-pair" : "",
    column.pair === "start" ? "haklai-results-pair-start" : "",
    column.pair === "end" ? "haklai-results-pair-end" : "",
    column.frame ? "haklai-results-frame" : ""
  ].filter(Boolean).join(" ");
}


const SHEKEL_LABEL = COMPACT_CURRENCY_LABELS.newShekel;

function getCompactCurrencyLabel(currency) {
  return COMPACT_CURRENCY_LABELS[currency] || SHEKEL_LABEL;
}

function formatCurrency(value, currencyLabel = SHEKEL_LABEL) {
  const formatted = formatHaklaiWholeMoney(value, { empty: "-" });
  if (!formatted || formatted === "-") return formatted;
  return (
    <>
      <span className="haklai-results-currency">{currencyLabel}</span>
      <span className="haklai-results-currency-value">{formatted}</span>
    </>
  );
}

function getPropertyKeyFromDeductions(deductions) {
  return deductions.find((deduction) => deduction?.haklaiPropertyKey)?.haklaiPropertyKey;
}

function getAcquisitionCurrencyLabels(mainCalc, draftPreCalc, analyzedPreCalc) {
  const fallbackCurrency = draftPreCalc?.acquisitionDetails?.deduCurrency
    || analyzedPreCalc?.acquisitionDetails?.deduCurrency;
  const byPropertyKey = {};
  const groups = mainCalc?.calculatedData?.deductionMat;
  if (Array.isArray(groups)) {
    groups.forEach((deductions) => {
      if (!Array.isArray(deductions) || deductions.length === 0) return;
      const propertyKey = getPropertyKeyFromDeductions(deductions);
      const purchase = deductions.find((deduction) => PURCHASE_DEDUCTION_TYPES.has(deduction?.deduType));
      const currency = purchase?.deduCurrency || fallbackCurrency;
      if (propertyKey && currency) {
        byPropertyKey[propertyKey] = getCompactCurrencyLabel(currency);
      }
    });
  }
  return {
    byPropertyKey,
    fallback: getCompactCurrencyLabel(fallbackCurrency)
  };
}

const EXPENSE_CURRENCY_ORDER = ["newShekel", "usd", "oldShekel", "lira"];

// הוצאות shows what the user entered: one line per currency, each the sum of
// that currency's original amounts. הוצאות ממודד stays a single shekel total.
function getNominalExpenseBreakdownByPropertyKey(mainCalc) {
  const groups = mainCalc?.calculatedData?.deductionMat;
  if (!Array.isArray(groups)) return {};
  return groups.reduce((byKey, deductions) => {
    if (!Array.isArray(deductions) || deductions.length === 0) return byKey;
    const propertyKey = getPropertyKeyFromDeductions(deductions);
    if (!propertyKey) return byKey;
    const totalsByCurrency = {};
    deductions.forEach((deduction) => {
      if (PURCHASE_DEDUCTION_TYPES.has(deduction?.deduType)) return;
      const amount = Number(deduction?.amount);
      if (!Number.isFinite(amount) || amount === 0) return;
      const currency = deduction?.deduCurrency || "newShekel";
      totalsByCurrency[currency] = (totalsByCurrency[currency] || 0) + amount;
    });
    const lines = Object.keys(totalsByCurrency)
      .sort((a, b) => {
        const rank = (currency) => {
          const index = EXPENSE_CURRENCY_ORDER.indexOf(currency);
          return index === -1 ? EXPENSE_CURRENCY_ORDER.length : index;
        };
        return rank(a) - rank(b);
      })
      .map((currency) => ({
        currency,
        label: getCompactCurrencyLabel(currency),
        amount: totalsByCurrency[currency]
      }));
    if (lines.length > 0) byKey[propertyKey] = lines;
    return byKey;
  }, {});
}

function getColumnCurrencyLabel(column, row, { acquisitionCurrency }) {
  if (column.key === "nominalAcquisitionPrice") {
    return acquisitionCurrency.byPropertyKey[row.propertyKey] || acquisitionCurrency.fallback;
  }
  return SHEKEL_LABEL;
}

function placeHaklaiResultsExtension(block) {
  const table = block.querySelector("table");
  const extension = block.querySelector(".haklai-results-extension");
  if (!table || !extension) return;
  const heads = [...table.querySelectorAll("thead th")].slice(-3);
  if (heads.length < 3) return;
  const container = block.querySelector(".haklai-results-property-table");
  const blockBox = block.getBoundingClientRect();
  const boxes = heads.map((head) => head.getBoundingClientRect());
  const left = boxes[2].left - blockBox.left - 1;
  const right = boxes[0].right - blockBox.left;
  const containerBottom = container
    ? container.getBoundingClientRect().bottom
    : boxes[0].bottom;
  extension.style.top = `${containerBottom - blockBox.top - 1}px`;
  extension.style.left = `${left}px`;
  extension.style.width = `${Math.max(right - left, 0)}px`;
  extension.style.setProperty(
    "--haklai-results-tail-columns",
    `${boxes[0].width}px ${boxes[1].width}px ${boxes[2].width + 1}px`
  );
  block.style.paddingBottom = `${extension.offsetHeight}px`;
}

function historicalCeilingWon(part) {
  return [
    [part?.historyTaxLinearSeif25, part?.finalLinearTaxAll24],
    [part?.historyTaxMutavSeif25, part?.finalMutavTaxAll24]
  ].some(([ceiling, ordinary]) => {
    const ceilingValue = Number(ceiling);
    const ordinaryValue = Number(ordinary);
    return Number.isFinite(ceilingValue)
      && Number.isFinite(ordinaryValue)
      && ceilingValue < ordinaryValue;
  });
}

function getHistoricalTrackByPropertyKey(mainCalc) {
  const parts = Array.isArray(mainCalc?.calculatedData?.dataPerPart)
    ? mainCalc.calculatedData.dataPerPart
    : [];
  return parts.reduce((byKey, part) => {
    const propertyKey = part?.haklaiPropertyKey;
    if (propertyKey && historicalCeilingWon(part)) byKey[propertyKey] = true;
    return byKey;
  }, {});
}

function getDisplayedColumnAmount(row, column, {
  shevachDisplayByPropertyKey,
  nominalExpensesByPropertyKey,
  historicalTrackByPropertyKey = {}
}) {
  if (column.key === "totalShevach") {
    return shevachDisplayByPropertyKey[row.propertyKey] ?? row.totalShevach;
  }
  if (column.key === "nominalExpenses") {
    return Number.isFinite(Number(row.nominalExpenses))
      ? row.nominalExpenses
      : nominalExpensesByPropertyKey[row.propertyKey];
  }
  if (column.key === "capitalGainsTax" && historicalTrackByPropertyKey[row.propertyKey]) {
    return row.payableTax;
  }
  return row[column.key];
}

function sumDisplayedColumn(rows, column, displayContext) {
  return rows.reduce((sum, row) => {
    const numericValue = Number(getDisplayedColumnAmount(row, column, displayContext));
    return sum + (Number.isFinite(numericValue) ? Math.round(numericValue) : 0);
  }, 0);
}

function getPropertyDisplay(row) {
  // The part name already identifies the row, for example "בית מגורים ראשי".
  // Prefixing the category repeats that name on every residence row.
  if (row.propertyLabel) return row.propertyLabel;
  return PROPERTY_TYPE_LABELS[row.propertyType] || row.propertyType || HAKLAI_ANALYSIS_RESULTS_LABELS.genericAsset;
}

function normalizePtorType(ptorType) {
  if (ptorType === HAKLAI_PTOR_TYPES.SINGLE) return HAKLAI_PTOR_TYPES.SINGLE;
  if (ptorType === HAKLAI_PTOR_TYPES.INHERITANCE) return HAKLAI_PTOR_TYPES.INHERITANCE;
  return HAKLAI_PTOR_TYPES.NONE;
}

function getAnalyzedPtorMeta(row, analyzedPreCalc) {
  const parts = Array.isArray(analyzedPreCalc?.preCalcPartsData)
    ? analyzedPreCalc.preCalcPartsData
    : [];
  const part = parts.find((candidate) => candidate?.haklaiPropertyKey === row?.propertyKey);
  if (!part) {
    return {
      hasPtorMetadata: false,
      ptorType: HAKLAI_PTOR_TYPES.NONE,
      hasTzinunOnPtor: false
    };
  }

  if (Object.prototype.hasOwnProperty.call(part, "haklaiPtorType")) {
    return {
      hasPtorMetadata: true,
      ptorType: normalizePtorType(part.haklaiPtorType),
      hasTzinunOnPtor: part.hasTzinunOnPtor === true
    };
  }

  if (part.isSinglePropertyPtorAllowed === true) {
    return {
      hasPtorMetadata: true,
      ptorType: HAKLAI_PTOR_TYPES.SINGLE,
      hasTzinunOnPtor: part.hasTzinunOnPtor === true
    };
  }

  if (part.isInheritancePtorAllowed === true) {
    return {
      hasPtorMetadata: true,
      ptorType: HAKLAI_PTOR_TYPES.INHERITANCE,
      hasTzinunOnPtor: false
    };
  }

  const hasPtorMetadata = (
    Object.prototype.hasOwnProperty.call(part, "isSinglePropertyPtorAllowed")
    || Object.prototype.hasOwnProperty.call(part, "isInheritancePtorAllowed")
  );
  return {
    hasPtorMetadata,
    ptorType: HAKLAI_PTOR_TYPES.NONE,
    hasTzinunOnPtor: false
  };
}

function getPropertyExemptionLabel(row, { haklaiDraftPreCalc, analyzedPreCalc } = {}) {
  const analyzedPtorMeta = getAnalyzedPtorMeta(row, analyzedPreCalc);
  const hasRowPtorType = Object.prototype.hasOwnProperty.call(row || {}, "ptorType");
  const rowPtorType = normalizePtorType(row?.ptorType);
  const ptorType = hasRowPtorType
    ? rowPtorType
    : analyzedPtorMeta.hasPtorMetadata
      ? analyzedPtorMeta.ptorType
      : row?.propertyKey === "residence_1"
        ? normalizePtorType(haklaiDraftPreCalc?.ptorType)
        : HAKLAI_PTOR_TYPES.NONE;
  const hasTzinunOnPtor = analyzedPtorMeta.hasTzinunOnPtor
    || (
      row?.propertyKey === "residence_1"
      && !analyzedPtorMeta.hasPtorMetadata
      && ptorType === normalizePtorType(haklaiDraftPreCalc?.ptorType)
      && haklaiDraftPreCalc?.hasTzinunOnPtor === true
    );

  if (ptorType === HAKLAI_PTOR_TYPES.NONE) {
    return "";
  }

  if (ptorType === HAKLAI_PTOR_TYPES.SINGLE && hasTzinunOnPtor) {
    return "";
  }

  if (ptorType === HAKLAI_PTOR_TYPES.SINGLE) {
    return HAKLAI_ANALYSIS_RESULTS_LABELS.singleApartmentExemption;
  }

  if (ptorType === HAKLAI_PTOR_TYPES.INHERITANCE) {
    return HAKLAI_ANALYSIS_RESULTS_LABELS.inheritanceExemption;
  }

  return "";
}

function getNominalDeductionAmount(deduction) {
  const convertedAmount = Number(deduction?.amountAfterCurrencyConversion);
  if (Number.isFinite(convertedAmount) && convertedAmount !== 0) return convertedAmount;
  const amount = Number(deduction?.amount);
  return Number.isFinite(amount) ? amount : 0;
}

function getNominalExpensesByPropertyKey(mainCalc) {
  const groups = mainCalc?.calculatedData?.deductionMat;
  if (!Array.isArray(groups)) return {};
  return groups.reduce((byKey, deductions) => {
    if (!Array.isArray(deductions) || deductions.length === 0) return byKey;
    const propertyKey = deductions[0]?.haklaiPropertyKey;
    if (!propertyKey) return byKey;
    byKey[propertyKey] = deductions.reduce((sum, deduction) => (
      PURCHASE_DEDUCTION_TYPES.has(deduction?.deduType)
        ? sum
        : sum + getNominalDeductionAmount(deduction)
    ), 0);
    return byKey;
  }, {});
}

function getDataPerPart(mainCalc) {
  return Array.isArray(mainCalc?.calculatedData?.dataPerPart) ? mainCalc.calculatedData.dataPerPart : [];
}

export function getHaklaiResultsPrisaData(mainCalc) {
  return getDataPerPart(mainCalc).reduce(
    (acc, part) => {
      const prisaYears = Array.isArray(part.prisaData)
        ? part.prisaData.reduce((maxYears, personPrisaData) => (
          Math.max(maxYears, Array.isArray(personPrisaData) ? personPrisaData.length : 0)
        ), 0)
        : 0;
      acc.maxPrisaYearsAllowed = Math.max(acc.maxPrisaYearsAllowed, prisaYears);
      acc.totalPrisaRev += (part.finalLinearBeforePrisa47 || 0) - (part.finalLinear47 || 0);
      acc.totalPrisaRev += (part.finalMutavBeforePrisa25 || 0) - (part.finalMutav25 || 0);
      acc.totalPrisaRev += (part.finalLinearBeforePrisa25 || 0) - (part.finalLinear25 || 0);
      acc.totalPrisaRev += (part.finalLinearBeforePrisa20 || 0) - (part.finalLinear20 || 0);
      acc.totalBeforePrisa += (part.finalLinearBeforePrisa47 || 0)
        + (part.finalMutavBeforePrisa25 || 0)
        + (part.finalLinearBeforePrisa25 || 0)
        + (part.finalLinearBeforePrisa20 || 0)
        + (part.finalInflationLinear10 || 0);
      acc.totalPreAfkaa += (part.finalLinearTaxAllPreAfkaa21 || 0) + (part.finalMutavTaxAllPreAfkaa21 || 0);
      return acc;
    },
    { totalPrisaRev: 0, maxPrisaYearsAllowed: 0, totalBeforePrisa: 0, totalPreAfkaa: 0 }
  );
}

function getAfkaaCredit(mainCalc) {
  return getDataPerPart(mainCalc).reduce(
    (sum, part) => sum + (part.finalLinearAfkaaZikui22 || 0) + (part.finalMutavAfkaaZikui22 || 0),
    0
  );
}

const OFFSET_TAX_BUCKETS = [
  { suffix: "Linear47", percentField: "firstLinearPercent", fallback: 0.47 },
  { suffix: "Linear20", percentField: "secondLinearPercent", fallback: 0.2 },
  { suffix: "Linear25", percentField: "thirdLinearPercent", fallback: 0.25 },
  { suffix: "Mutav25", percentField: "mutavPercent", fallback: 0.25 },
  { suffix: "Inflation10", percentField: "inflationPercent", fallback: 0.1 }
];

function readPartTaxRate(part, fieldName, fallback) {
  const raw = part?.[fieldName];
  const parsed = typeof raw === "string" ? parseFloat(raw.replace("%", "")) : Number(raw);
  if (!Number.isFinite(parsed) || parsed <= 0) return fallback;
  return parsed > 1 ? parsed / 100 : parsed;
}

function getPositiveAmount(value) {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) && numericValue > 0 ? numericValue : 0;
}

function getRabinovichSavings(mainCalc) {
  const hasPrisa = mainCalc?.prisa?.shouldTryPrisa === true;
  return getDataPerPart(mainCalc).reduce((sum, part) => {
    const percent = Number(part?.discountRabPercent) || 0;
    if (!(percent > 0) || percent >= 1) return sum;
    const discountedTax = hasPrisa
      ? (part.finalLinearBeforePrisa47 || 0)
        + (part.finalMutavBeforePrisa25 || 0)
        + (part.finalLinearBeforePrisa25 || 0)
        + (part.finalLinearBeforePrisa20 || 0)
      : (part.finalLinear47 || 0)
        + (part.finalMutav25 || 0)
        + (part.finalLinear25 || 0)
        + (part.finalLinear20 || 0);
    if (!(discountedTax > 0)) return sum;
    return sum + (discountedTax * percent) / (1 - percent);
  }, 0);
}

function getOffsetTaxSavings(mainCalc, offsetPrefix) {
  return getDataPerPart(mainCalc).reduce((sum, part) => (
    sum + OFFSET_TAX_BUCKETS.reduce((bucketSum, bucket) => {
      const offsetShevach = Number(part?.[`${offsetPrefix}${bucket.suffix}`]) || 0;
      if (!(offsetShevach > 0)) return bucketSum;
      return bucketSum + offsetShevach * readPartTaxRate(part, bucket.percentField, bucket.fallback);
    }, 0)
  ), 0);
}

function getTaxBeforeAdjustments(totalTaxToPay, variantRows) {
  const payable = Number(totalTaxToPay);
  const adjustmentsTotal = variantRows.reduce((sum, row) => {
    const amount = Number(row.amount);
    return sum + (Number.isFinite(amount) ? Math.round(amount) : 0);
  }, 0);
  return (Number.isFinite(payable) ? Math.round(payable) : 0) + adjustmentsTotal;
}

function formatHaklaiAdjustmentLabel(label) {
  const detail = String(label).replace(/^הנחת\s+/, "");
  return `${HAKLAI_ANALYSIS_RESULTS_LABELS.adjustmentReason} ${detail}`;
}

// Same count classic shows as תקופת פריסה מומלצת. One year is singular; every other count is plural.
export function getHaklaiPrisaYearUnit(yearCount) {
  return Number(yearCount) === 1 ? "שנה" : "שנים";
}

export function getHaklaiResultsVariantRows(mainCalc) {
  const hasPrisa = mainCalc?.prisa?.shouldTryPrisa === true;
  const hasAfkaa = mainCalc?.afkaa === true;
  const prisaData = hasPrisa ? getHaklaiResultsPrisaData(mainCalc) : null;
  const prisaSavings = prisaData ? prisaData.totalPrisaRev : 0;
  const prisaYearCount = prisaData ? prisaData.maxPrisaYearsAllowed : 0;

  return [
    {
      key: "rabinovichDiscount",
      label: HAKLAI_ANALYSIS_RESULTS_LABELS.rabinovichDiscount,
      amount: getRabinovichSavings(mainCalc)
    },
    {
      key: "businessLossOffset",
      label: HAKLAI_ANALYSIS_RESULTS_LABELS.businessLossOffset,
      amount: getOffsetTaxSavings(mainCalc, "kizuzIski")
    },
    {
      key: "capitalLossOffset",
      label: HAKLAI_ANALYSIS_RESULTS_LABELS.capitalLossOffset,
      amount: getOffsetTaxSavings(mainCalc, "kizuzHoni")
    },
    {
      key: "prisaSavings",
      label: HAKLAI_ANALYSIS_RESULTS_LABELS.prisaSavings,
      yearCount: prisaYearCount,
      amount: getPositiveAmount(prisaSavings)
    },
    {
      key: "afkaaCredit",
      label: HAKLAI_ANALYSIS_RESULTS_LABELS.afkaaCredit,
      amount: hasAfkaa ? getPositiveAmount(getAfkaaCredit(mainCalc)) : 0
    }
  ];
}

const HaklaiAnalysisResultsView = ({
  dealId,
  haklaiAnalysis,
  haklaiDraftPreCalc,
  analyzedPreCalc,
  analyzedMainCalc,
  onBackToCalc
}) => {
  const rows = Array.isArray(haklaiAnalysis?.resultsByProperty) ? haklaiAnalysis.resultsByProperty : [];
  const summary = haklaiAnalysis?.summary || {};
  const summaryRaw = haklaiAnalysis?.summaryRaw || {};
  const nominalExpensesByPropertyKey = getNominalExpensesByPropertyKey(analyzedMainCalc);
  const acquisitionCurrency = getAcquisitionCurrencyLabels(
    analyzedMainCalc,
    haklaiDraftPreCalc,
    analyzedPreCalc
  );
  const nominalExpenseBreakdownByPropertyKey = getNominalExpenseBreakdownByPropertyKey(analyzedMainCalc);
  const currencyContext = { acquisitionCurrency };
  let shevachDisplayByPropertyKey = {};
  const rowsHaveShevach = rows.every((row) => Number.isFinite(Number(row?.totalShevach)));
  if (rowsHaveShevach) {
    try {
      shevachDisplayByPropertyKey = buildHaklaiPayableTaxDisplayByPropertyKey(
        rows,
        summaryRaw.totalShevach ?? summary.totalShevach ?? 0,
        "totalShevach"
      );
    } catch {
      shevachDisplayByPropertyKey = {};
    }
  }
  const isFullyExemptAnalysis = isHaklaiAnalysisFullyExempt(analyzedPreCalc);

  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, [dealId, haklaiAnalysis?.generatedAt]);

  const columnDisplayContext = {
    shevachDisplayByPropertyKey,
    nominalExpensesByPropertyKey,
    historicalTrackByPropertyKey: getHistoricalTrackByPropertyKey(analyzedMainCalc)
  };
  const totalShevach = sumDisplayedColumn(
    rows,
    columns.find((column) => column.key === "totalShevach"),
    columnDisplayContext
  );
  const variantRows = getHaklaiResultsVariantRows(analyzedMainCalc);
  const totalTaxToPay = summaryRaw.totalTaxToPay ?? summary.totalTaxToPay;
  const taxBeforeAdjustments = getTaxBeforeAdjustments(totalTaxToPay, variantRows);
  const propertyBlockRef = useRef(null);
  const [adjustmentsOpen, setAdjustmentsOpen] = useState(false);
  const reserveAdjustmentsSpace = (collapseNode, expanded) => {
    const block = propertyBlockRef.current;
    const extension = block?.querySelector(".haklai-results-extension");
    if (!block || !extension || !collapseNode) return;
    const fixedRowsHeight = extension.offsetHeight - collapseNode.offsetHeight;
    block.style.paddingBottom = `${
      fixedRowsHeight + (expanded ? collapseNode.scrollHeight : 0)
    }px`;
  };

  useLayoutEffect(() => {
    const block = propertyBlockRef.current;
    if (!block) return undefined;
    const place = () => placeHaklaiResultsExtension(block);
    place();
    if (typeof ResizeObserver === "undefined") return undefined;
    const table = block.querySelector("table");
    if (!table) return undefined;
    const observer = new ResizeObserver(place);
    observer.observe(table);
    return () => observer.disconnect();
  }, [rows, totalShevach, taxBeforeAdjustments, totalTaxToPay, variantRows.length]);
  return (
    <Box className="haklai-results deal-sum-calc">
      <Box className="haklai-results-toolbar">
        <Button className="haklai-results-back" onClick={onBackToCalc}>
          <KeyboardArrowRightIcon />
          <span>{HAKLAI_ANALYSIS_RESULTS_LABELS.backToCalc}</span>
        </Button>
      </Box>

      {!rows.length ? (
        <Box className="haklai-results-empty">
          <Typography variant="body1">
            {HAKLAI_ANALYSIS_RESULTS_MESSAGES.noResults}
          </Typography>
        </Box>
      ) : (
        <div className="pre-calc-sections haklai-results-sections">
          <CustomAcordion summaryText={HAKLAI_ANALYSIS_RESULTS_LABELS.resultsTitle} defaultExpanded={true}>
            <div className="haklai-results-property-block" ref={propertyBlockRef}>
            <TableContainer className="haklai-results-table haklai-data-table haklai-results-property-table">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {columns.map((column) => (
                      <TableCell
                        key={column.key}
                        align={column.key === "propertyType" ? "right" : "center"}
                        className={getResultsColumnClassName(column, { includeWidth: true })}
                      >
                        {column.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row) => {
                    const propertyExemptionLabel = getPropertyExemptionLabel(row, {
                      haklaiDraftPreCalc,
                      analyzedPreCalc
                    });
                    return (
                      <TableRow key={row.propertyKey}>
                        <TableCell align="right" className="haklai-results-property-col haklai-results-frame">
                          <div className="haklai-results-property">
                            <Typography variant="body2" className="haklai-results-property-name">
                              {getPropertyDisplay(row)}
                            </Typography>
                            {propertyExemptionLabel ? (
                              <Typography variant="caption" className="haklai-results-property-meta">
                                {propertyExemptionLabel}
                              </Typography>
                            ) : null}
                          </div>
                        </TableCell>
                        {columns.slice(1).map((column) => {
                          const expenseLines = column.key === "nominalExpenses"
                            ? nominalExpenseBreakdownByPropertyKey[row.propertyKey]
                            : null;
                          if (expenseLines) {
                            return (
                              <TableCell
                                key={column.key}
                                align="center"
                                className={getResultsColumnClassName(column) || undefined}
                              >
                                <div className="haklai-results-amount-lines">
                                  {expenseLines.map((line) => (
                                    <Typography
                                      key={line.currency}
                                      variant="body2"
                                      className="haklai-results-amount"
                                    >
                                      {formatCurrency(line.amount, line.label)}
                                    </Typography>
                                  ))}
                                </div>
                              </TableCell>
                            );
                          }
                          const amount = getDisplayedColumnAmount(row, column, columnDisplayContext);
                          return (
                            <TableCell
                              key={column.key}
                              align="center"
                              className={getResultsColumnClassName(column) || undefined}
                            >
                              <Typography
                                variant="body2"
                                className={`haklai-results-amount${column.isTotal ? " is-total" : ""}`}
                              >
                                {formatCurrency(amount, getColumnCurrencyLabel(column, row, currencyContext))}
                              </Typography>
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <div className="haklai-results-extension">
              <div className="haklai-results-extension-row is-summary" role="row">
                <div className="haklai-results-extension-label" role="cell">
                  {HAKLAI_ANALYSIS_RESULTS_LABELS.tableTotal}
                </div>
                <div className="haklai-results-amount is-total" role="cell">
                  {formatCurrency(totalShevach)}
                </div>
                <div className="haklai-results-amount is-total" role="cell">
                  {formatCurrency(taxBeforeAdjustments)}
                </div>
              </div>
              <Collapse
                className="haklai-results-adjustments-collapse"
                in={adjustmentsOpen}
                onEnter={(node) => reserveAdjustmentsSpace(node, true)}
                onEntered={() => placeHaklaiResultsExtension(propertyBlockRef.current)}
                onExit={(node) => reserveAdjustmentsSpace(node, false)}
                onExited={() => placeHaklaiResultsExtension(propertyBlockRef.current)}
                timeout={260}
                unmountOnExit
              >
                <div role="rowgroup">
                  {variantRows.map((variantRow) => (
                    <div className="haklai-results-extension-row" key={variantRow.key} role="row">
                      <div className="haklai-results-extension-label is-wide" role="cell">
                        {formatHaklaiAdjustmentLabel(variantRow.label)}
                        {variantRow.yearCount > 0 ? (
                          <span className="haklai-results-prisa-period">
                            {`ל-${variantRow.yearCount}\u00A0${getHaklaiPrisaYearUnit(variantRow.yearCount)}`}
                          </span>
                        ) : null}
                      </div>
                      <div className="haklai-results-amount" role="cell">
                        {formatCurrency(variantRow.amount)}
                      </div>
                    </div>
                  ))}
                </div>
              </Collapse>
              <div className="haklai-results-extension-row is-payable" role="row">
                <div className="haklai-results-extension-label" role="cell">
                  <span>{HAKLAI_ANALYSIS_RESULTS_LABELS.totalTaxToPay}</span>
                </div>
                <div className="haklai-results-adjustments-cell" role="cell">
                  <Button
                    className="haklai-results-adjustments-toggle"
                    aria-expanded={adjustmentsOpen}
                    onClick={() => setAdjustmentsOpen((open) => !open)}
                  >
                    <span>{HAKLAI_ANALYSIS_RESULTS_LABELS.showAdjustments}</span>
                    {adjustmentsOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                  </Button>
                </div>
                <div className="haklai-results-amount is-total" role="cell">
                  {formatCurrency(totalTaxToPay)}
                </div>
              </div>
            </div>
            </div>
          </CustomAcordion>

          {isFullyExemptAnalysis ? (
            <HaklaiNoTaxOutcome />
          ) : (
            <HaklaiResults2990Panel
              dealId={dealId}
              haklaiDraftPreCalc={haklaiDraftPreCalc}
              analyzedPreCalc={analyzedPreCalc}
            />
          )}
        </div>
      )}
    </Box>
  );
};

export default React.memo(HaklaiAnalysisResultsView);
